import * as React from 'react';
import Map from '../../components/Maps/Map';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';

import {IonAlert, IonContent, IonIcon, IonPage} from '@ionic/react';
import {mapSharp, shieldCheckmarkSharp, statsChartSharp, timerSharp, warningSharp} from 'ionicons/icons';

import './Dashboard.css';
import Tour from 'reactour';
import tourService from '../../services/tourService';
import {useHistory} from 'react-router';
import {useEffect, useState} from 'react';
import firebaseService from '../../services/firebaseService';
import {DashboardData} from '../../interfaces/DashboardData';
import {CountyData} from '../../interfaces/CountyData';
import {setAuthToken} from '../../services/bigQueryService';

import Panel from '../../components/ui/Panel';
import StatTile from '../../components/ui/StatTile';

import CrashesDataCard from '../../components/Cards/CrashesCard';
import CongestionDataCard from '../../components/Cards/Congestioncard';
import SafetyCard from '../../components/Cards/SafetyCard';
import IncidentImpactCard from '../../components/Cards/IncidentImpactCard';

const initialCounties: CountyData[] = [
    {
        name: "St. Louis (City)",
        crashes: 50,
    },
    {
        name: "St.Louis",
        crashes: 18,
    },
    {
        name: "Jefferson",
        crashes: 5,
    },
    {
        name: "St. Clair",
        crashes: 3,
    },
    {
        name: "St. Charles",
        crashes: 3,
    }
];

/** Thousands separators for readable headline numbers; non-numerics pass through. */
function formatValue(raw: string): string {
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed.toLocaleString('en-US') : raw;
}

interface InfoTarget {
    header: string;
    subHeader: string;
    message: string;
    route: string;
}

const Dashboard: React.FC = () => {
    const [dashboardData, setDashboardData] = useState<DashboardData>(new DashboardData());
    const [counties, setCounties] = useState<CountyData[]>(initialCounties);
    const steps = tourService.getStepsFor('Dashboard');
    const isTour = tourService.StartTour();
    const history = useHistory();
    const [lastUpdated, setLastUpdated] = useState(new Date().toLocaleString());

    const [newdashboard, setnewdashboard] = useState(null);
    const [newdatacards, setnewdatacards] = useState<any>([]);

    // One alert serves the whole KPI row instead of one per card.
    const [info, setInfo] = useState<InfoTarget | null>(null);

    useEffect(() => {
        firebaseService.getAuthToken().then(doc => {
            setAuthToken(doc.data()?.token);
        });
        const unsubscribe = firebaseService.watchDashboardData().onSnapshot(doc => {
            const dashboardData: DashboardData = doc.docs[0].data() as DashboardData;

            const newdashboard: any = doc.docs[0].data();
            if (dashboardData) {
                setDashboardData(dashboardData);
                dashboardData.countyCrashes.sort((a, b) => a.crashes - b.crashes);
                setCounties(dashboardData.countyCrashes.reverse());
                setLastUpdated(new Date(dashboardData.lastUpdated.value * 1000).toLocaleString());
            }
            if (newdashboard) {
                setnewdashboard(newdashboard);
                setnewdatacards([
                    {
                        title: "Crashes This Week",
                        data: dashboardData.weeklyCrashes.value.toString(),
                        updated: lastUpdated,
                        source: dashboardData.weeklyCrashes.notes.source.toString(),
                        description: dashboardData.weeklyCrashes.notes.Description.toString(),
                        route: '/AppCenter/TranscoreAnalytics',
                    },
                    {
                        // Was clearance time, which measured how long an
                        // incident stayed in a feed. This is the delay each
                        // vehicle actually loses passing one.
                        title: "Incident Delay",
                        data: (newdashboard.perVehicleDelay?.value ?? 0).toString(),
                        unit: "sec",
                        updated: lastUpdated,
                        source: newdashboard.perVehicleDelay?.notes?.source ?? "N/A",
                        description: newdashboard.perVehicleDelay?.notes?.Description ?? "N/A",
                        route: '/AppCenter/ClearanceTime',
                    },
                    {
                        // Was freeway counts, a sum of detector volumes that
                        // only a third of rows carry and that KC reports far
                        // more often than St Louis -- it tracked reporting
                        // coverage, not traffic.
                        title: "Fatalities YTD",
                        data: (newdashboard.fatalitiesYtd?.value ?? 0).toString(),
                        updated: lastUpdated,
                        source: newdashboard.fatalitiesYtd?.notes?.source ?? "N/A",
                        description: newdashboard.fatalitiesYtd?.notes?.Description ?? "N/A",
                        route: 'AppCenter/TranscoreAnalytics',
                    },
                    {
                        title: "PTI",
                        // Was a hard-coded "3.8" with no source. The planning
                        // time index is computed from HERE probe travel times
                        // now, so show whatever the job published.
                        data: (newdashboard.ptiv?.value ?? newdashboard.pti?.value ?? 0).toString(),
                        updated: lastUpdated,
                        source: newdashboard.ptiv?.notes?.source ?? "N/A",
                        description: newdashboard.ptiv?.notes?.Description ?? "N/A",
                        route: '/AppCenter/Congestion',
                    }
                ])
            }
        });
        return () => {
            unsubscribe();
        }
    }, []);

    /** Matches the old cards: a tile with no source has nothing to explain. */
    const openInfo = (card: any) => {
        if (card.source === "N/A") {
            return;
        }
        setInfo({
            header: card.title,
            subHeader: "Source: " + card.source,
            message: card.description,
            route: card.route,
        });
    };

    const hasData = newdashboard && newdatacards.length > 0;

    return (
        <IonPage>
            <Header title="Dashboard" />
            <IonContent fullscreen className="page-content">
                <div className="page-body dashboard">
                    <div className="page-body__meta">
                        <h1 className="page-body__title">Traffic overview</h1>
                        <span>Updated {lastUpdated}</span>
                    </div>

                    {hasData && (
                        <div className="dashboard__kpis">
                            <StatTile
                                label={newdatacards[0].title}
                                value={formatValue(newdatacards[0].data)}
                                accent="var(--domain-crashes)"
                                icon={<IonIcon icon={warningSharp} />}
                                meta="Past 7 days"
                                trend={dashboardData.dailyCrashCount}
                                onInfo={() => openInfo(newdatacards[0])}
                            />
                            <StatTile
                                label={newdatacards[1].title}
                                value={formatValue(newdatacards[1].data)}
                                unit={newdatacards[1].unit}
                                accent="var(--domain-clearance)"
                                icon={<IonIcon icon={timerSharp} />}
                                meta="Per vehicle, past day"
                                onInfo={() => openInfo(newdatacards[1])}
                            />
                            <StatTile
                                label={newdatacards[2].title}
                                value={formatValue(newdatacards[2].data)}
                                accent="var(--domain-freeway)"
                                icon={<IonIcon icon={shieldCheckmarkSharp} />}
                                meta={`vs ${(newdashboard as any)?.fatalitiesByYear?.slice(-2)[0] ?? '-'} last year`}
                                trend={(newdashboard as any)?.fatalitiesByYear?.slice(0, -1)}
                                onInfo={() => openInfo(newdatacards[2])}
                            />
                            <StatTile
                                label={newdatacards[3].title}
                                value={newdatacards[3].data}
                                accent="var(--domain-congestion)"
                                icon={<IonIcon icon={statsChartSharp} />}
                                meta="Planning time index, past 14 days"
                                trend={(newdashboard as any)?.ptiTrend}
                                onInfo={() => openInfo(newdatacards[3])}
                            />
                        </div>
                    )}

                    {hasData && (
                        <div className="dashboard__grid">
                            <CrashesDataCard content={newdatacards[0]} crashList={counties} />
                            <SafetyCard content={newdatacards[0]} newdata={newdashboard} />
                            <IncidentImpactCard content={newdatacards[0]} newdata={newdashboard} />
                            <CongestionDataCard content={newdatacards[3]} crashList={counties} newdata={newdashboard} />
                        </div>
                    )}

                    <Panel
                        title="Live map"
                        className="third-step"
                        accent="var(--domain-crashes)"
                        icon={<IonIcon icon={mapSharp} />}
                    >
                        <Map height="30em" traffic={true} location={true} stateOutline={true} />
                    </Panel>

                    <Footer />
                </div>
            </IonContent>

            <IonAlert
                isOpen={info !== null}
                header={info?.header}
                subHeader={info?.subHeader}
                message={info?.message}
                buttons={[{
                    text: "More Information",
                    handler: () => {
                        const route = info?.route;
                        setInfo(null);
                        if (route) {
                            history.push(route);
                        }
                    }
                }]}
                onDidDismiss={() => setInfo(null)}
                cssClass="bigger-alert"
            />

            <Tour
                steps={steps}
                isOpen={isTour}
                accentColor='black'
                onRequestClose={() => { tourService.GoBack(history) }} />
        </IonPage>
    );
};

export default Dashboard;
